import React from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { EDITOR_COLORS, editorText, webControlStyle } from '../../../styles/editorTheme';
import { OutlineCheckboxBox, renderWebOutlineCheckbox } from '../../OutlineCheckbox';

export interface ManageAccountsSearchValues {
  accountName: string;
  accountId: string;
  accountGroup: string;
  billpayProcessor: string;
  showAll: boolean;
  blocksize: string;
}

export const ACCOUNT_BLOCKSIZE_OPTIONS = ['10', '20', '30'] as const;
export const DEFAULT_ACCOUNT_BLOCKSIZE = '10';

export interface AccountFilterOption {
  value: string;
  label: string;
}

interface FilterSelectProps {
  value: string;
  options: AccountFilterOption[];
  allLabel: string;
  accessibilityLabel: string;
  onChange: (value: string) => void;
}

// Dropdown for a filter whose values come from the SkinTypeDef; '' means "no filter".
export const FilterSelect: React.FC<FilterSelectProps> = ({ value, options, allLabel, accessibilityLabel, onChange }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);

  if (Platform.OS === 'web') {
    const webStyle = { ...webControlStyle, width: '100%', height: 36, borderRadius: 6, padding: '0 8px' } as any;
    return (
      <View style={filterSelectStyles.fill}>
        {React.createElement(
          'select' as any,
          {
            value,
            onChange: (event: any) => onChange(String(event?.target?.value ?? '')),
            style: webStyle,
            'aria-label': accessibilityLabel,
          },
          [
            React.createElement('option' as any, { key: '', value: '' }, allLabel),
            ...options.map((option) => React.createElement('option' as any, { key: option.value, value: option.value }, option.label)),
          ]
        )}
      </View>
    );
  }

  const selected = options.find((option) => option.value === value);
  return (
    <View style={[filterSelectStyles.fill, filterSelectStyles.wrap]}>
      <TouchableOpacity
        onPress={() => setMenuOpen((open) => !open)}
        style={filterSelectStyles.btn}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
      >
        <Text style={filterSelectStyles.btnText} numberOfLines={1}>{selected ? selected.label : allLabel}</Text>
        <Text style={filterSelectStyles.chevron}>{menuOpen ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {menuOpen ? (
        <View style={filterSelectStyles.menu}>
          {[{ value: '', label: allLabel }, ...options].map((option) => (
            <Pressable
              key={option.value || '__all'}
              style={[filterSelectStyles.option, option.value === value && filterSelectStyles.optionActive]}
              onPress={() => {
                onChange(option.value);
                setMenuOpen(false);
              }}
              accessibilityRole="button"
              accessibilityLabel={option.label}
            >
              <Text style={filterSelectStyles.optionText}>{option.label}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
};

interface ManageAccountsSearchBarProps {
  accountGroupOptions?: AccountFilterOption[];
  billpayProcessorOptions?: AccountFilterOption[];
  values: ManageAccountsSearchValues;
  onValuesChange: (values: ManageAccountsSearchValues) => void;
  onSearch: (values: ManageAccountsSearchValues) => void;
  isLoading: boolean;
  // Omit to hide the Add New Account button.
  onAddNewAccount?: () => void;
}

const EMPTY: ManageAccountsSearchValues = {
  accountName: '',
  accountId: '',
  accountGroup: '',
  billpayProcessor: '',
  showAll: false,
  blocksize: DEFAULT_ACCOUNT_BLOCKSIZE,
};

export const ManageAccountsSearchBar: React.FC<ManageAccountsSearchBarProps> = ({
  accountGroupOptions = [],
  billpayProcessorOptions = [],
  values,
  onValuesChange,
  onSearch,
  isLoading,
  onAddNewAccount,
}) => {
  const [displayMenuOpen, setDisplayMenuOpen] = React.useState(false);

  const setField = (field: keyof ManageAccountsSearchValues, value: string | boolean) => {
    onValuesChange({ ...values, [field]: value });
  };

  const handleSearch = () => {
    onSearch(values);
  };

  // Group/Processor dropdowns search immediately on change, using the new value
  // (state hasn't updated yet when onSearch fires).
  const changeFilterAndSearch = (field: 'accountGroup' | 'billpayProcessor', value: string) => {
    const next = { ...values, [field]: value };
    onValuesChange(next);
    onSearch(next);
  };

  const handleClear = () => {
    onValuesChange({ ...EMPTY, blocksize: values.blocksize });
  };

  const hasValues = [values.accountName, values.accountId, values.accountGroup, values.billpayProcessor]
    .some(v => v.trim().length > 0);
  const isWeb = Platform.OS === 'web';

  const displayValue = (ACCOUNT_BLOCKSIZE_OPTIONS as readonly string[]).includes(values.blocksize)
    ? values.blocksize
    : DEFAULT_ACCOUNT_BLOCKSIZE;

  const selectBlocksize = (blocksize: string) => {
    onValuesChange({ ...values, blocksize });
    setDisplayMenuOpen(false);
  };

  const webSelectStyle = { ...webControlStyle, width: 50, borderRadius: 5, padding: '0 6px' } as any;

  return (
    <View style={styles.container}>
      {/* Row 1: Search, Account Name, Account Id, Show All, Display */}
      <View style={styles.searchRow}>
        <View style={styles.searchCol}>
          <TouchableOpacity
            onPress={handleSearch}
            style={[styles.searchBtn, isLoading && styles.searchBtnDisabled]}
            disabled={isLoading}
            accessibilityLabel="Search accounts"
          >
            {isLoading ? (
              <ActivityIndicator size="small" color={EDITOR_COLORS.submitText} />
            ) : (
              <Text style={styles.searchBtnText} numberOfLines={1}>🔍 Search</Text>
            )}
          </TouchableOpacity>
        </View>

        <View style={styles.spacer} />

        <View style={styles.nameGroup}>
          <Text style={styles.labelName}>Account Name</Text>
          <TextInput
            style={styles.input}
            value={values.accountName}
            onChangeText={v => setField('accountName', v)}
            autoCapitalize="words"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
            placeholder="Enter account name"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.spacer} />

        <View style={styles.idGroup}>
          <Text style={styles.labelId}>Account Id</Text>
          <TextInput
            style={styles.input}
            value={values.accountId}
            onChangeText={v => setField('accountId', v)}
            autoCapitalize="none"
            returnKeyType="search"
            onSubmitEditing={handleSearch}
            placeholder="Account ID"
            placeholderTextColor={EDITOR_COLORS.placeholder}
          />
        </View>

        <View style={styles.spacer} />

        <View style={styles.checkboxGroup}>
          {isWeb ? (
            <View style={styles.checkboxRow}>
              <View style={styles.checkboxBoxGap}>
                {renderWebOutlineCheckbox(values.showAll, (next) => setField('showAll', next), 'Show all accounts')}
              </View>
              <Text style={styles.checkboxLabel}>Show All</Text>
            </View>
          ) : (
            <Pressable
              onPress={() => setField('showAll', !values.showAll)}
              style={styles.checkboxRow}
              accessibilityRole="checkbox"
              accessibilityLabel="Show all accounts"
            >
              <View style={styles.checkboxBoxGap}>
                <OutlineCheckboxBox checked={values.showAll} />
              </View>
              <Text style={styles.checkboxLabel}>Show All</Text>
            </Pressable>
          )}
        </View>

        <View style={styles.spacer} />

        <View style={styles.displayRow}>
          <Text style={styles.displayLabel}>Display</Text>
          {isWeb ? (
            React.createElement(
              'select' as any,
              {
                value: displayValue,
                onChange: (event: any) => selectBlocksize(String(event?.target?.value ?? DEFAULT_ACCOUNT_BLOCKSIZE)),
                style: webSelectStyle,
                'aria-label': 'Set display block size',
              },
              ACCOUNT_BLOCKSIZE_OPTIONS.map((option) => (
                React.createElement('option' as any, { key: option, value: option }, option)
              ))
            )
          ) : (
            <View style={styles.dropdownWrap}>
              <TouchableOpacity
                onPress={() => setDisplayMenuOpen((open) => !open)}
                style={styles.dropdownBtn}
                accessibilityRole="button"
                accessibilityLabel="Set display block size"
              >
                <Text style={styles.dropdownBtnText}>{displayValue}</Text>
                <Text style={styles.dropdownChevron}>{displayMenuOpen ? '▲' : '▼'}</Text>
              </TouchableOpacity>

              {displayMenuOpen ? (
                <View style={styles.dropdownMenu}>
                  {ACCOUNT_BLOCKSIZE_OPTIONS.map((option) => (
                    <Pressable
                      key={option}
                      style={[styles.dropdownOption, displayValue === option && styles.dropdownOptionActive]}
                      onPress={() => selectBlocksize(option)}
                      accessibilityRole="button"
                      accessibilityLabel={`Set display ${option}`}
                    >
                      <Text
                        style={[
                          styles.dropdownOptionText,
                          displayValue === option && styles.dropdownOptionTextActive,
                        ]}
                      >
                        {option}
                      </Text>
                    </Pressable>
                  ))}
                </View>
              ) : null}
            </View>
          )}
        </View>

        {hasValues && (
          <TouchableOpacity
            onPress={handleClear}
            style={styles.clearBtn}
            accessibilityLabel="Clear search"
          >
            <Text style={styles.clearBtnText}>Clear</Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Row 2: Account Group (under Account Name), Billpay Processor (under Account Id) */}
      <View style={[styles.searchRow, styles.secondRow]}>
        <View style={styles.searchCol} />

        <View style={styles.spacer} />

        <View style={styles.nameGroup}>
          <Text style={styles.labelName}>Account Group</Text>
          <FilterSelect
            value={values.accountGroup}
            options={accountGroupOptions}
            allLabel="All groups"
            accessibilityLabel="Account group"
            onChange={v => changeFilterAndSearch('accountGroup', v)}
          />
        </View>

        <View style={styles.spacer} />

        <View style={styles.idGroup}>
          <Text style={styles.labelId}>Processor</Text>
          <FilterSelect
            value={values.billpayProcessor}
            options={billpayProcessorOptions}
            allLabel="All"
            accessibilityLabel="Processor"
            onChange={v => changeFilterAndSearch('billpayProcessor', v)}
          />
        </View>

        {onAddNewAccount ? (
          <>
            <View style={styles.spacer} />

            <TouchableOpacity
              onPress={onAddNewAccount}
              style={styles.addBtn}
              accessibilityLabel="Add new account"
            >
              <Text style={styles.addBtnText} numberOfLines={1}>➕ Add New Account</Text>
            </TouchableOpacity>
          </>
        ) : null}
      </View>
    </View>
  );
};

const filterSelectStyles = StyleSheet.create({
  fill: {
    flex: 1,
    minWidth: 0,
  },
  wrap: {
    position: 'relative',
  },
  btn: {
    height: 36,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    paddingHorizontal: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
    gap: 6,
  },
  btnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
    flex: 1,
  },
  chevron: {
    fontFamily: 'Arial',
    fontSize: 14,
    color: EDITOR_COLORS.textMuted,
  },
  menu: {
    position: 'absolute',
    top: 38,
    left: 0,
    right: 0,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    zIndex: 1000,
    elevation: 10,
    overflow: 'hidden',
  },
  option: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  optionActive: {
    backgroundColor: '#eff6ff',
  },
  optionText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});

const styles = StyleSheet.create({
  container: {
    paddingVertical: 16,
    paddingHorizontal: 20,
    backgroundColor: EDITOR_COLORS.background,
    borderBottomWidth: 1,
    borderBottomColor: EDITOR_COLORS.separator,
  },
  spacer: {
    width: 12,
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  secondRow: {
    marginTop: 10,
  },
  searchCol: {
    width: 130,
  },
  nameGroup: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  idGroup: {
    width: 250,
    flexDirection: 'row',
    alignItems: 'center',
  },
  labelName: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    width: 105,
    textAlign: 'right',
    paddingRight: 8,
  },
  labelId: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
    width: 115,
    textAlign: 'right',
    paddingRight: 8,
  },
  addBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  addBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  input: {
    ...editorText,
    flex: 1,
    minWidth: 0,
    height: 36,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 6,
    backgroundColor: EDITOR_COLORS.background,
    color: EDITOR_COLORS.textPrimary,
  },
  checkboxGroup: {
    justifyContent: 'center',
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  checkboxBoxGap: {
    marginRight: 6,
  },
  checkboxLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  displayRow: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  displayLabel: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  dropdownWrap: {
    position: 'relative',
  },
  dropdownBtn: {
    width: 50,
    height: 26,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 5,
    paddingHorizontal: 7,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: EDITOR_COLORS.background,
    gap: 6,
  },
  dropdownBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textPrimary,
  },
  dropdownChevron: {
    fontFamily: 'Arial',
    fontSize: 14,
    color: EDITOR_COLORS.textMuted,
  },
  dropdownMenu: {
    position: 'absolute',
    top: 28,
    right: 0,
    minWidth: 60,
    borderWidth: 1,
    borderColor: EDITOR_COLORS.controlBorder,
    borderRadius: 5,
    backgroundColor: EDITOR_COLORS.background,
    zIndex: 1000,
    elevation: 10,
    overflow: 'hidden',
  },
  dropdownOption: {
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  dropdownOptionActive: {
    backgroundColor: '#eff6ff',
  },
  dropdownOptionText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
  dropdownOptionTextActive: {
    color: EDITOR_COLORS.accent,
  },
  searchBtn: {
    paddingHorizontal: 20,
    paddingVertical: 8,
    backgroundColor: EDITOR_COLORS.submitBackground,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 130,
  },
  searchBtnDisabled: {
    opacity: 0.6,
  },
  searchBtnText: {
    ...editorText,
    color: EDITOR_COLORS.submitText,
  },
  clearBtn: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginLeft: 8,
    backgroundColor: EDITOR_COLORS.submitBackgroundDisabled,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  clearBtnText: {
    ...editorText,
    color: EDITOR_COLORS.textSecondary,
  },
});
